import { useLang } from 'hooks/lang';

import { openSampleProjectUrl } from 'entities';

import {
  Tag,
  Row,
  DropdownMenu,
  DropdownMenuItem,
  Button,
  ResponsiveOverlay,
} from 'components/Blocks';

import {
  FolderTreeIcon,
  LineChartIcon,
  PencilIcon,
  SettingsIcon,
  UserPlusIcon,
} from 'components/Icons';

import { useUrlState } from 'hooks/urlState';

import { useTaskOption } from '../options';
import { TaskOptionOverlayProps } from '../createTaskOptions';

export function SampleProjectsModal({ close, isOpen, task }: TaskOptionOverlayProps) {
  const { t } = useLang();

  const { updateUrlParams } = useUrlState();

  const requestRecruitment = useTaskOption('REQUEST_RECRUITMENT', task.id);
  const createSampleProject = useTaskOption('CREATE_SAMPLE_PROJECT', task.id);

  const hasIds = !!task?.sampleProjectIds?.length;

  return (
    <ResponsiveOverlay
      isOpen={isOpen}
      close={close}
      title="Sample Projects"
      width="min(80vw, 440px)"
      preventOpenAutoFocus
      allowEscape
    >
      <Row gap="2" alignItems="flex-start" flexWrap="wrap">
        {hasIds ? (
          <>
            {task.sampleProjectIds?.map((id) => (
              <DropdownMenu
                key={id}
                align="start"
                title="Options"
                trigger={
                  <button>
                    <Tag name={id} color="primary" round={false} size="medium" />
                  </button>
                }
              >
                {[
                  {
                    id: 'edit',
                    icon: <PencilIcon />,
                    label: t('edit'),
                    onClick: () => openSampleProjectUrl(id, 'edit'),
                  },
                  {
                    id: 'stats',
                    icon: <LineChartIcon />,
                    label: t('stats'),
                    onClick: () => openSampleProjectUrl(id, 'stats'),
                  },
                  {
                    id: 'change-status',
                    icon: <SettingsIcon />,
                    label: t('change-status'),
                    onClick: () => openSampleProjectUrl(id, 'status-change'),
                  },
                  {
                    id: 'request-recruitment',
                    icon: <UserPlusIcon />,
                    label: t('request-recruitment'),
                    hide: !requestRecruitment.isEnabled,
                    onClick: () => {
                      updateUrlParams({ lastSampleId: id });
                      requestRecruitment.action();
                    },
                  },
                ].map(
                  ({ icon, label, id, onClick, hide }) =>
                    !hide && (
                      <DropdownMenuItem key={id} onClick={onClick}>
                        {icon}
                        {label}
                      </DropdownMenuItem>
                    ),
                )}
              </DropdownMenu>
            ))}
          </>
        ) : (
          <p>{t('no-project-was-created')}</p>
        )}
      </Row>

      {createSampleProject.isEnabled && (
        <Button
          onClick={createSampleProject.action}
          loading={createSampleProject.loading}
          className="ml-auto"
          variant="outlined"
        >
          <FolderTreeIcon />
          {t('create-project')}
        </Button>
      )}
    </ResponsiveOverlay>
  );
}
